import dayjs from 'dayjs';
import useCashAvailable from 'hooks/Bank/useCashAvailable';
import useVATReturnFrequency from 'hooks/shared/useVATReturnFrequency';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENT_QUARTER, CURRENT_YEAR } from 'utils/constants';
import roundNumber from 'utils/roundNumber';
import {
  evaluateProvisionForSpecialVATLabels,
  evaluateProvisionForVATLabels,
} from '../CashAvailable/helpers';

const useTotalTaxesToSetAside = () => {
  const { t } = useTranslation();

  const userVATFrequency = useVATReturnFrequency();

  const cashAvailableQuery = useCashAvailable();

  const taxes = useMemo(() => {
    const {
      socialContributions,
      provisionForIncomeTax,
      provisionForCompanyTax,
      provisionForVAT,
      provisionForSpecialVAT,
    } = cashAvailableQuery.data || {};

    const VATLabels = evaluateProvisionForVATLabels(
      userVATFrequency,
      provisionForVAT?.currentPeriod?.amount || 0,
      provisionForVAT?.pastPeriods?.amount || 0,
    );

    const specialVATLabel = evaluateProvisionForSpecialVATLabels(
      userVATFrequency,
      provisionForSpecialVAT?.currentPeriod?.amount || 0,
      provisionForSpecialVAT?.pastPeriods?.amount || 0,
    );

    const allTaxes = [
      {
        key: 'socialContributions.currentPeriod',
        label: t('taxes.provision_social_contributions_current_quarter', {
          quarter: CURRENT_QUARTER,
        }),
        value: socialContributions?.currentPeriod,
      },
      {
        key: 'socialContributions.pastPeriods',
        label: t('taxes.provision_social_contributions_past_quarters'),
        value: socialContributions?.pastPeriods,
      },
      {
        key: 'provisionForIncomeTax.currentPeriod',
        label: t(
          (provisionForIncomeTax?.currentPeriod?.amount || 0) > 0
            ? `cash_available.income_tax_reimbursment.current_year`
            : 'provision_for_income_taxes_year',
          {
            year: CURRENT_YEAR,
          },
        ),
        value: provisionForIncomeTax?.currentPeriod,
      },
      {
        key: 'provisionForIncomeTax.pastPeriods',
        label: t(
          (provisionForIncomeTax?.pastPeriods?.amount || 0) > 0
            ? `cash_available.income_tax_reimbursment.past_years`
            : 'provision_for_income_taxes_year_past_years',
        ),
        value: provisionForIncomeTax?.pastPeriods,
      },
      {
        key: 'provisionForCompanyTax.currentPeriod',
        label: t(
          (provisionForCompanyTax?.currentPeriod?.amount || 0) > 0
            ? 'cash_available.corporate_tax_reimbursment.current_year'
            : 'taxes.provision_company_tax.current',
          { year: dayjs().year() },
        ),
        value: provisionForCompanyTax?.currentPeriod,
      },
      {
        key: 'provisionForCompanyTax.pastPeriods',
        label: t(
          (provisionForCompanyTax?.pastPeriods?.amount || 0) > 0
            ? 'cash_available.corporate_tax_reimbursment.past_years'
            : 'taxes.provision_company_tax.past',
        ),
        value: provisionForCompanyTax?.pastPeriods,
      },
      {
        key: 'provisionForVAT.currentPeriod',
        label: VATLabels.currentPeriodLabel,
        value: provisionForVAT?.currentPeriod,
      },
      {
        key: 'provisionForVAT.pastPeriods',
        label: VATLabels.pastPeriodsLabel,
        value: provisionForVAT?.pastPeriods,
      },
      {
        key: 'provisionForSpecialVAT.currentPeriod',
        label: specialVATLabel.currentPeriodLabel,
        value: provisionForSpecialVAT?.currentPeriod,
      },
      {
        key: 'provisionForSpecialVAT.pastPeriods',
        label: specialVATLabel.pastPeriodsLabel,
        value: provisionForSpecialVAT?.pastPeriods,
      },
    ];

    const userTaxes = allTaxes.filter(({ value }) => !!value);

    const taxesToPay = userTaxes.filter(({ value }) => value!.amount <= 0);

    return taxesToPay;
  }, [cashAvailableQuery.data, t, userVATFrequency]);

  const totalTaxesToSetAside = roundNumber(
    taxes
      .map((tax) => tax?.value?.amount || 0)
      .reduce((prev, current) => prev + current, 0) * -1,
  );

  return {
    isLoading: cashAvailableQuery.isLoading || cashAvailableQuery.isRefetching,
    totalTaxesToSetAside,
  };
};

export default useTotalTaxesToSetAside;
